/// <reference types="user-agent-data-types" />
import { from, Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';

export enum BrowserEngine {
  blink = 'Blink',
  gecko = 'Gecko',
  webkit = 'WebKit',
  unknown = 'Unknown',
}
export enum BrowserBrand {
  chrome = 'Chrome',
  edge = 'Edge',
  safari = 'Safari',
  firefox = 'Firefox',
  opera = 'Opera',
  brave = 'Brave',
  duckduckgo = 'DuckDuckGo',
  samsung = 'Samsung',
  unknown = 'Unknown',
}

export interface BrowserInfo {
  engine: BrowserEngine;
  brand: BrowserBrand;
  version: string | null;
  // True when running on iOS regardless of the browser brand
  // (Chrome/Firefox/DuckDuckGo on iOS are all WebKit underneath)
  isIOSWrappedBrowser: boolean;
}

// -----------------------------------------------------------------
// UA-CH path (Chromium family)
// -----------------------------------------------------------------

// Order matters — check specific brands before generic Chromium
const BRAND_MAP: [RegExp, BrowserBrand][] = [
  [/DuckDuckGo/i, BrowserBrand.duckduckgo],
  [/Brave/i, BrowserBrand.brave],
  [/Microsoft Edge/i, BrowserBrand.edge],
  [/Opera|OPR/i, BrowserBrand.opera],
  [/Samsung/i, BrowserBrand.samsung],
  [/Google Chrome/i, BrowserBrand.chrome],
];

function resolveBrandFromUACH(brands: NavigatorUABrandVersion[]): BrowserBrand {
  for (const { brand } of brands) {
    for (const [regex, result] of BRAND_MAP) {
      if (regex.test(brand)) return result;
    }
  }
  return BrowserBrand.unknown;
}

function resolveFromUACH(brands: NavigatorUABrandVersion[], hints: UADataValues): BrowserInfo {
  return {
    engine: BrowserEngine.blink,
    brand: resolveBrandFromUACH(brands),
    version: hints.uaFullVersion ?? null,
    isIOSWrappedBrowser: false,
  };
}

// -----------------------------------------------------------------
// UA string fallback (Firefox, Safari, iOS)
// -----------------------------------------------------------------

function detectBrowserFromUA(ua: string): BrowserInfo {
  const s = ua.toLowerCase();

  // iOS detection first — must come before Safari/Chrome checks
  // because all iOS browsers include "Safari" in their UA.
  // iPadOS 13+ spoofs a macOS UA, so also check touch points.
  const isIOS = /iphone|ipad|ipod/.test(s) || (/macintosh/.test(s) && navigator.maxTouchPoints > 1);

  if (s.includes('fxios')) {
    return {
      engine: BrowserEngine.webkit,
      brand: BrowserBrand.firefox,
      version: extractVersion(ua, /FxiOS\/([\d.]+)/),
      isIOSWrappedBrowser: true,
    };
  }
  if (s.includes('crios')) {
    return {
      engine: BrowserEngine.webkit,
      brand: BrowserBrand.chrome,
      version: extractVersion(ua, /CriOS\/([\d.]+)/),
      isIOSWrappedBrowser: true,
    };
  }
  if (s.includes('firefox')) {
    return {
      engine: BrowserEngine.gecko,
      brand: BrowserBrand.firefox,
      version: extractVersion(ua, /Firefox\/([\d.]+)/),
      isIOSWrappedBrowser: false,
    };
  }
  if (s.includes('safari') && !s.includes('chrome')) {
    return {
      engine: BrowserEngine.webkit,
      brand: BrowserBrand.safari,
      version: extractVersion(ua, /Version\/([\d.]+)/),
      isIOSWrappedBrowser: isIOS,
    };
  }

  return {
    engine: BrowserEngine.unknown,
    brand: BrowserBrand.unknown,
    version: null,
    isIOSWrappedBrowser: isIOS,
  };
}

function extractVersion(ua: string, regex: RegExp): string | null {
  return ua.match(regex)?.[1] ?? null;
}

// -----------------------------------------------------------------
// Public API
// -----------------------------------------------------------------

export function detectBrowser(): Observable<BrowserInfo> {
  if (navigator.userAgentData) {
    return from(navigator.userAgentData.getHighEntropyValues(['uaFullVersion'])).pipe(
      map((hints) => resolveFromUACH(navigator.userAgentData!.brands, hints)),
    );
  }

  return of(detectBrowserFromUA(navigator.userAgent));
}
